import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod/v3';
import { profileApi } from '@/api/admin/profiles';
import type { CreateUserDTO } from '@/api/admin/users';
import { userApi } from '@/api/admin/users';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormSkeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import { maskCpf } from '@/lib/formatters';

const schema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  username: z.string().min(1, 'Usuário é obrigatório'),
  email: z.string().email('Email inválido'),
  cpf: z.string().min(11, 'CPF inválido'),
  profileId: z.string().min(1, 'Selecione um perfil'),
  external: z.boolean(),
});

type FormData = z.infer<typeof schema>;

export function UserCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sendToEmailOverride, setSendToEmailOverride] = useState('');

  const { data: profilesData, isLoading: isLoadingProfiles } = useQuery({
    queryKey: ['profiles'],
    queryFn: () => profileApi.findMany({ limit: 100 }),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      username: '',
      email: '',
      cpf: '',
      profileId: '',
      external: false,
    },
  });

  const profileIdValue = form.watch('profileId');
  const selectedProfile = useMemo(
    () => profilesData?.docs?.find((p) => p.id === profileIdValue) ?? null,
    [profilesData?.docs, profileIdValue],
  );

  const watchedEmail = form.watch('email');

  const mutation = useMutation({
    mutationFn: (data: CreateUserDTO) => userApi.create(data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: 'Sucesso',
        description: `Usuário criado. Email enviado para ${variables.sendToEmail || variables.email}.`,
      });
      navigate('/admin/users');
    },
  });

  const onSubmit = (data: FormData) => {
    mutation.mutate({
      ...data,
      sendToEmail: sendToEmailOverride || data.email,
    });
  };

  if (isLoadingProfiles) {
    return (
      <div>
        <Card>
          <CardHeader>
            <CardTitle>Carregando...</CardTitle>
          </CardHeader>
          <CardContent>
            <FormSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Voltar"
          onClick={() => navigate('/admin/users')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Novo Usuário</h1>
          <p className="text-sm text-gray-500">Preencha os dados para criar um novo usuário.</p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* External User Checkbox */}
              <FormField
                control={form.control}
                name="external"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Usuário externo</FormLabel>
                    </div>
                  </FormItem>
                )}
              />

              {/* Profile Selection */}
              <FormField
                control={form.control}
                name="profileId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Perfil</FormLabel>
                    <FormControl>
                      <Combobox
                        data-testid="admin-users-create-combobox-profile"
                        data={profilesData?.docs ?? []}
                        keyExtractor={(profile) => profile.id}
                        displayValueGetter={(profile) => profile.name}
                        type="single"
                        value={selectedProfile ? [selectedProfile] : []}
                        onOptionChange={(selected) => {
                          field.onChange(selected[0]?.id ?? '');
                        }}
                        placeholder="Selecione o perfil"
                        isLoading={isLoadingProfiles}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cpf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CPF</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="000.000.000-00"
                          {...field}
                          onChange={(e) => {
                            field.onChange(maskCpf(e.target.value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome completo</FormLabel>
                      <FormControl>
                        <Input placeholder="Digite o nome completo" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Digite o email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Usuário</FormLabel>
                      <FormControl>
                        <Input placeholder="Digite o nome de usuário" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Email para envio do link de acesso */}
              <div className="border-t pt-4 space-y-2">
                <Label htmlFor="sendToEmail">Email para envio do link de acesso</Label>
                <p className="text-sm text-gray-500">
                  Caso deseje enviar o link de acesso para um email diferente, altere abaixo.
                </p>
                <Input
                  id="sendToEmail"
                  type="email"
                  placeholder="Email para envio"
                  value={sendToEmailOverride || watchedEmail}
                  onChange={(e) => setSendToEmailOverride(e.target.value)}
                />
              </div>

              {mutation.isError && (
                <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">
                  {mutation.error instanceof Error
                    ? mutation.error.message
                    : 'Erro ao criar usuário'}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/admin/users')}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Criando...' : 'Criar Usuário'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </motion.div>
  );
}
